import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FriendIcon, AddBoldIcon } from 'app/components/shared/icons';
import CheckIcon from 'app/components/shared/icons/check-icon';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import {
  selectedPeopleSelector,
  setSelectedPeople,
  pushSelectedPeople,
  IPerson,
  PEOPLE_CONTEXT,
  contextSelector,
} from 'store/reducers/people/people-reducer';
import { ThemeProvider } from 'styled-components';

import {
  CollectiveDirectoryPersonDrawerActionsSectionRow,
  CollectiveDirectoryPersonDrawerActionsSectionRowInfoContainer,
  CollectiveDirectoryPersonDrawerActionsSectionRowInfoIconContainer,
  CollectiveDirectoryPersonDrawerActionsSectionRowInfoCircle,
  CollectiveDirectoryPersonDrawerActionsSectionRowInfoText,
  CollectiveDirectoryPersonDrawerActionsSectionRowCTAContainer,
  CollectiveDirectoryPersonDrawerSelectWrapper,
  CollectiveDirectoryPersonDrawerSelectButton,
  CollectiveDirectoryPersonDrawerSelectButtonCircle,
  CollectiveDirectoryPersonDrawerSelectButtonText,
} from '../PeopleDrawer/styled';

interface AddToContactListCTAProps {
  person: IPerson;
  closeDrawer?: boolean;
}

export default function AddToContactListCTA({ person, closeDrawer }: AddToContactListCTAProps) {
  const dispatch = useDispatch();
  const context = useSelector(contextSelector);
  const selectedPeople = useSelector(selectedPeopleSelector);
  const teamingDrawerAddBehavioralFunction = useBehavioral(BEHAVIORALS.TEAMING_DRAWER_ADD);

  const isPersonSelected = useMemo(() => {
    return selectedPeople.find((selectedPerson) => selectedPerson.uuid === person.uuid);
  }, [selectedPeople, person]);

  const hasNoPointOfContact = useMemo(() => {
    return person.primary_teaming_leader === null && context === PEOPLE_CONTEXT.TEAMING;
  }, [person, context]);

  const handleAddClick = () => {
    if (isPersonSelected) {
      dispatch(
        setSelectedPeople(
          selectedPeople.filter((selectedPerson) => selectedPerson.uuid !== person.uuid)
        )
      );
    } else {
      teamingDrawerAddBehavioralFunction({
        related_object_uuid: person.uuid,
      });
      dispatch(pushSelectedPeople([person]));
    }

    if (closeDrawer) {
      window.postMessage('drawers:close', '*');
    }
  };

  return (
    <CollectiveDirectoryPersonDrawerActionsSectionRow>
      <CollectiveDirectoryPersonDrawerActionsSectionRowInfoContainer>
        <CollectiveDirectoryPersonDrawerActionsSectionRowInfoIconContainer>
          <CollectiveDirectoryPersonDrawerActionsSectionRowInfoCircle>
            <FriendIcon height={24} width={24} fill="var(--color-primary)" />
          </CollectiveDirectoryPersonDrawerActionsSectionRowInfoCircle>
        </CollectiveDirectoryPersonDrawerActionsSectionRowInfoIconContainer>
        <CollectiveDirectoryPersonDrawerActionsSectionRowInfoText>
          {isPersonSelected
            ? `${person.name.split(' ')[0]} has been added to your contact list.`
            : `Add ${person.name.split(' ')[0]} to your list and keep browsing for good fits.`}
        </CollectiveDirectoryPersonDrawerActionsSectionRowInfoText>
      </CollectiveDirectoryPersonDrawerActionsSectionRowInfoContainer>
      <CollectiveDirectoryPersonDrawerActionsSectionRowCTAContainer>
        <ThemeProvider
          theme={{
            isPersonSelected,
          }}
        >
          <CollectiveDirectoryPersonDrawerSelectWrapper>
            <CollectiveDirectoryPersonDrawerSelectButton
              onClick={handleAddClick}
              disabled={hasNoPointOfContact}
            >
              {isPersonSelected ? (
                <CheckIcon
                  fill={'var(--color-primary)'}
                  withoutBorder
                  width={24}
                  height={24}
                  background="transparent"
                />
              ) : (
                <CollectiveDirectoryPersonDrawerSelectButtonCircle>
                  <AddBoldIcon fill="var(--color-primary)" />
                </CollectiveDirectoryPersonDrawerSelectButtonCircle>
              )}
              <CollectiveDirectoryPersonDrawerSelectButtonText>
                {isPersonSelected ? 'ADDED' : 'ADD'}
              </CollectiveDirectoryPersonDrawerSelectButtonText>
            </CollectiveDirectoryPersonDrawerSelectButton>
          </CollectiveDirectoryPersonDrawerSelectWrapper>
        </ThemeProvider>
      </CollectiveDirectoryPersonDrawerActionsSectionRowCTAContainer>
    </CollectiveDirectoryPersonDrawerActionsSectionRow>
  );
}
